import { useCallback } from "react";
import { useApi } from "./useApi";

/** The two category node types the Classification blade manages. */
export type CategoryType = "technology" | "application";

export interface Category {
  id: string;
  name: string;
  sources: Record<string, unknown>;
}

export interface Pagination {
  limit: number;
  count: number;
  next: string | null;
}

export interface CategoryPage {
  data: Category[];
  pagination: Pagination;
}

export interface Capability {
  sub: string;
  can_manage: boolean;
}

/** Map a category type to its BFF route prefix. */
const ROUTE: Record<CategoryType, string> = {
  technology: "/technology-categories",
  application: "/application-categories",
};

/**
 * Read the canonical error envelope ({ error_code, message, correlation_id })
 * from a failed BFF response and surface its message. Falls back to the status
 * text when the body is not the expected shape.
 */
async function toError(response: Response): Promise<Error> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === "string" && body.message) {
      return new Error(body.message);
    }
  } catch {
    // fall through to status text
  }
  return new Error(`Request failed (${response.status} ${response.statusText}).`);
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await toError(response);
  }
  return (await response.json()) as T;
}

export interface ClassificationApi {
  getCapability: () => Promise<Capability>;
  list: (type: CategoryType, opts?: { name?: string; cursor?: string }) => Promise<CategoryPage>;
  create: (type: CategoryType, name: string) => Promise<Category>;
  rename: (type: CategoryType, id: string, name: string) => Promise<Category>;
  remove: (type: CategoryType, id: string) => Promise<void>;
}

/**
 * Typed client for the Classification BFF, bound to the current access token
 * via useApi. All write methods target routes the BFF gates on the admin group;
 * a non-elevated caller receives a 403 that surfaces as a thrown Error.
 */
export function useClassification(): ClassificationApi {
  const api = useApi();

  const getCapability = useCallback(async (): Promise<Capability> => {
    return readJson<Capability>(await api("/me"));
  }, [api]);

  const list = useCallback(
    async (type: CategoryType, opts: { name?: string; cursor?: string } = {}) => {
      const params = new URLSearchParams();
      if (opts.name) params.set("name", opts.name);
      if (opts.cursor) params.set("cursor", opts.cursor);
      const query = params.toString();
      const path = query ? `${ROUTE[type]}?${query}` : ROUTE[type];
      return readJson<CategoryPage>(await api(path));
    },
    [api],
  );

  const create = useCallback(
    async (type: CategoryType, name: string) => {
      return readJson<Category>(
        await api(ROUTE[type], { method: "POST", body: JSON.stringify({ name }) }),
      );
    },
    [api],
  );

  const rename = useCallback(
    async (type: CategoryType, id: string, name: string) => {
      return readJson<Category>(
        await api(`${ROUTE[type]}/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify({ name }),
        }),
      );
    },
    [api],
  );

  const remove = useCallback(
    async (type: CategoryType, id: string) => {
      const response = await api(`${ROUTE[type]}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw await toError(response);
      }
    },
    [api],
  );

  return { getCapability, list, create, rename, remove };
}
